import type { JobState } from './job-selectors';
import { STARTING, SUCCESS, FAILURE } from '../action/util/action-name-creators';

import {
  Action,
  updateResourceByObjectKey,
  updateStatus,
  updateStatusById,
  setStatusDirtyById,
  StateStatus,
  updateResourceByParam,
  setStateDirtyByParam,
  setStatusByParam,
} from './utils/state-manager';
import { convertArrayToMap } from '../util/convert-array-to-map';

const InitialState: JobState = {
  jobsById: {},
  deletedJobsByOrgId: {},
  jobsStateById: {},
  jobsStateByOrgId: {},
  deletedJobsStateByOrgId: {},
  jobsStateByAccountId: {},
  applicantSummaryByJobId: {},
  applicantSummaryByJobIdStatus: {},
  adSetsByJobId: {},
  notificationTypes: null,
  notificationTypesLoading: false,
  labelsByJobId: {},
  labelsByJobIdStatus: {},
  commonFinalizingHireSteps: [],
  commonFinalizingHireStepsStatus: StateStatus.notAttempted,
  jobDetailsByJobId: {},
  jobDetailsByJobIdStatus: {},
  usersByJobId: {},
  usersByJobIdStatus: {},
  jobRefreshDatesById: {},
  jobRefreshDatesStateByOrgId: {},
  orgDefaultNotificationSettingsByOrgId: {},
  orgDefaultNotificationSettingsStateByOrgId: {},
};

const updateJobData = (state: JobState, jobId: number, update: Partial<Job>) => {
  if (Object.values(update).length === 0) {
    return state;
  }
  const jobsById = { ...state.jobsById };
  const existingJob = jobsById[jobId];
  if (existingJob) {
    jobsById[jobId] = { ...existingJob, ...update };
  }
  return { ...state, jobsById };
};

export default (state = InitialState, action: Action) => {
  switch (action.type) {
    case STARTING('LOAD_JOB'):
    case STARTING('UPDATE_JOB_STATUS'): {
      const { jobId } = action.payload.args;
      return updateResourceByParam(state, 'jobs', 'id', jobId, true);
    }
    case SUCCESS('LOAD_JOB'): {
      const {
        args: { jobId },
        data,
      } = action.payload;
      return updateResourceByParam(
        state,
        'jobs',
        'id',
        jobId,
        false,
        convertArrayToMap(data, (j: Job) => j.job_id.toString()),
      );
    }
    case SUCCESS('UPDATE_JOB_STATUS'): {
      const {
        args: { jobId },
        data: { job },
      } = action.payload;
      return updateResourceByParam(state, 'jobs', 'id', jobId, false, { [jobId.toString()]: job });
    }
    case FAILURE('LOAD_JOB'):
    case FAILURE('UPDATE_JOB_STATUS'): {
      const { jobId } = action.payload.args;
      return updateResourceByParam(state, 'jobs', 'id', jobId, false);
    }
    case STARTING('LOAD_ORG_JOBS'): {
      const { orgId } = action.payload.args;
      return updateResourceByParam(state, 'jobs', 'orgId', orgId, true);
    }
    case SUCCESS('LOAD_ORG_JOBS'): {
      const {
        args: { orgId },
        data,
      } = action.payload;
      return updateResourceByParam(
        state,
        'jobs',
        'orgId',
        orgId,
        false,
        convertArrayToMap(data, (j: Job) => j.job_id.toString()),
      );
    }
    case FAILURE('LOAD_ORG_JOBS'): {
      const { orgId } = action.payload.args;
      return updateResourceByParam(state, 'jobs', 'orgId', orgId, false);
    }
    case STARTING('LOAD_ORG_DELETED_JOBS'): {
      const { orgId } = action.payload.args;
      return updateResourceByObjectKey(state, 'deletedJobsByOrgId', orgId, true);
    }
    case SUCCESS('LOAD_ORG_DELETED_JOBS'): {
      const {
        args: { orgId },
        data: { jobs },
      } = action.payload;
      return updateResourceByObjectKey(state, 'deletedJobsByOrgId', orgId, false, {
        [orgId]: jobs,
      });
    }
    case FAILURE('LOAD_ORG_DELETED_JOBS'): {
      const { orgId } = action.payload.args;
      return updateResourceByObjectKey(state, 'deletedJobsByOrgId', orgId, false);
    }
    case STARTING('LOAD_ACCOUNT_JOBS'): {
      const { accountId } = action.payload.args;
      return updateResourceByParam(state, 'jobs', 'accountId', accountId, true);
    }
    case SUCCESS('LOAD_ACCOUNT_JOBS'): {
      const {
        args: { accountId },
        data,
      } = action.payload;
      return updateResourceByParam(
        state,
        'jobs',
        'accountId',
        accountId,
        false,
        convertArrayToMap(data, (j: Job) => j.job_id.toString()),
      );
    }
    case FAILURE('LOAD_ACCOUNT_JOBS'): {
      const { accountId } = action.payload.args;
      return updateResourceByParam(state, 'jobs', 'accountId', accountId, false);
    }
    case SUCCESS('CREATE_DEMO_JOB'):
    case SUCCESS('ADD_JOB'): {
      const {
        data: { job },
      } = action.payload;
      const jobsById = { ...state.jobsById };
      jobsById[job.job_id] = job;
      return { ...state, jobsById };
    }
    case STARTING('JOB_APPLICANT_SUMMARY'): {
      return updateStatusById(state, 'applicantSummaryByJobId', true, action.payload.args.jobId);
    }
    case SUCCESS('JOB_APPLICANT_SUMMARY'): {
      return updateStatusById(
        state,
        'applicantSummaryByJobId',
        false,
        action.payload.args.jobId,
        action.payload.data,
      );
    }
    case FAILURE('JOB_APPLICANT_SUMMARY'): {
      return updateStatusById(state, 'applicantSummaryByJobId', false, action.payload.args.jobId);
    }
    case 'SET_JOB_APPLICANT_SUMMARY_DIRTY': {
      const { jobId } = action.payload.args;
      return setStatusDirtyById(state, 'applicantSummaryByJobId', jobId);
    }
    case 'LOAD_JOB_AD_SETS_SUCCESS': {
      const adSetsByJobId = { ...state.adSetsByJobId };
      const { jobId, adSets } = action.payload;
      adSetsByJobId[jobId] = adSets;
      return { ...state, adSetsByJobId };
    }
    case SUCCESS('APPLICATION_STATUS'): {
      const applicantSummary = { ...state.applicantSummaryByJobId };
      let newState = { ...state };
      const { jobId } = action.payload.args;

      if (applicantSummary[jobId]) {
        newState = setStatusDirtyById(state, 'applicantSummaryByJobId', jobId);
      }

      return {
        ...newState,
        applicantSummaryByJobId: applicantSummary,
      };
    }
    case SUCCESS('UPDATE_JOB_MONTHLY_CREDIT_DOLLARS'): {
      const { jobId, monthlyCreditDollars } = action.payload.args;
      return updateJobData(state, jobId, {
        monthlyCreditDollars,
      });
    }
    case SUCCESS('UPDATE_JOB_IS_DETERMINING_FACEBOOK_CPA'): {
      const { jobId, isDeterminingFacebookCPA } = action.payload.args;
      const update: Record<string, any> = { isDeterminingFacebookCPA };
      if (!isDeterminingFacebookCPA) {
        update.determineFacebookCPACreditDailyDollars = 0;
      }
      return updateJobData(state, jobId, update);
    }
    case SUCCESS('UPDATE_JOB'): {
      const jobId = action.payload.data.job.job_id;

      // Reload the labels because a job update could have changed them
      state.labelsByJobIdStatus = { ...state.labelsByJobIdStatus, [jobId]: StateStatus.dirty };

      if (state.jobDetailsByJobId && state.jobDetailsByJobId[jobId]) {
        state.jobDetailsByJobIdStatus = {
          ...state.jobDetailsByJobIdStatus,
          [jobId]: StateStatus.dirty,
        };
      }

      return updateJobData(state, jobId, action.payload.data.job);
    }
    case STARTING('LOAD_JOB_USERS'): {
      return updateStatusById(state, 'usersByJobId', true, action.payload.args.jobId);
    }
    case SUCCESS('LOAD_JOB_USERS'): {
      return updateStatusById(
        state,
        'usersByJobId',
        false,
        action.payload.args.jobId,
        action.payload.data,
      );
    }
    case FAILURE('LOAD_JOB_USERS'): {
      return updateStatusById(state, 'usersByJobId', false, action.payload.args.jobId);
    }
    case 'LOAD_NOTIFICATION_TYPES_STARTING': {
      return { ...state, notificationTypesLoading: true };
    }
    case 'LOAD_NOTIFICATION_TYPES_FAILURE': {
      return { ...state, notificationTypesLoading: false };
    }
    case 'LOAD_NOTIFICATION_TYPES_SUCCESS': {
      return {
        ...state,
        notificationTypes: action.payload.notificationTypes,
        notificationTypesLoading: false,
      };
    }
    case 'SAVE_AD_SPEND_SUCCESS': {
      const { jobAdSpend }: { jobAdSpend: Array<JobAdSpend> } = action.payload;
      return jobAdSpend.reduce(
        (s, jobSpend) =>
          updateJobData(s, jobSpend.jobId, {
            currentSpend: jobSpend.adSpend,
          }),
        state,
      );
    }
    case SUCCESS('DELETE_JOB'): {
      const { jobId, orgId } = action.payload.args;

      const newJobs = { ...state.jobsById };
      if (newJobs[jobId]) {
        delete newJobs[jobId];
      }
      const newState = { ...state, jobsById: newJobs };
      return setStateDirtyByParam(newState, 'deletedJobs', 'orgId', orgId);
    }
    case SUCCESS('RESTORE_DELETED_JOB'): {
      const {
        args: { jobId, orgId },
        data: { job },
      } = action.payload;
      const jobsById = { ...state.jobsById };
      jobsById[jobId] = job;
      const deletedJobsByOrgId = { ...state.deletedJobsByOrgId };
      deletedJobsByOrgId[orgId] = deletedJobsByOrgId[orgId]?.filter((j) => j.job_id !== jobId);
      return { ...state, jobsById, deletedJobsByOrgId };
    }
    case STARTING('LABELS'): {
      return updateStatusById(state, 'labelsByJobId', true, action.payload.args.jobId);
    }
    case SUCCESS('LABELS'): {
      return updateStatusById(
        state,
        'labelsByJobId',
        false,
        action.payload.args.jobId,
        action.payload.data,
      );
    }
    case FAILURE('LABELS'): {
      return updateStatusById(state, 'labelsByJobId', false, action.payload.args.jobId);
    }
    case STARTING('JOB_COMMON_FINALIZING_HIRE_STEPS'): {
      return updateStatus(state, 'commonFinalizingHireSteps', true);
    }
    case SUCCESS('JOB_COMMON_FINALIZING_HIRE_STEPS'): {
      return updateStatus(state, 'commonFinalizingHireSteps', false, action.payload.data);
    }
    case FAILURE('JOB_COMMON_FINALIZING_HIRE_STEPS'): {
      return updateStatus(state, 'commonFinalizingHireSteps', false);
    }
    case STARTING('LOAD_JOB_DETAILS'): {
      return updateStatusById(state, 'jobDetailsByJobId', true, action.payload.args.jobId);
    }
    case SUCCESS('LOAD_JOB_DETAILS'): {
      return updateStatusById(
        state,
        'jobDetailsByJobId',
        false,
        action.payload.args.jobId,
        action.payload.data,
      );
    }
    case FAILURE('LOAD_JOB_DETAILS'): {
      return updateStatusById(state, 'jobDetailsByJobId', false, action.payload.args.jobId);
    }
    case SUCCESS('CREATE_NOTIFICATION_SETTINGS'): {
      const { result } = action.payload.data;

      if (
        !result.jobId ||
        result.notificationType !== 'APPLICANT_REACH_OUT' ||
        !state.jobDetailsByJobId[result.jobId]
      ) {
        return state;
      }
      const existingNotificationSetting = (
        state.jobDetailsByJobId[result.jobId].notificationSettings || []
      ).find((ns) => ns.notificationSettingId === result.notificationSettingId);
      if (existingNotificationSetting) {
        return state;
      }

      return {
        ...state,
        jobDetailsByJobId: {
          ...state.jobDetailsByJobId,
          [result.jobId]: {
            ...state.jobDetailsByJobId[result.jobId],
            notificationSettings: [
              ...(state.jobDetailsByJobId[result.jobId].notificationSettings || []),
              result,
            ],
          },
        },
      };
    }
    case SUCCESS('POPULATE_NOTIFICATION_SETTINGS_FROM_ORG'): {
      const jobDetailsByJobId = { ...state.jobDetailsByJobId };
      jobDetailsByJobId[action.payload.args.jobId] = action.payload.data;
      return { ...state, jobDetailsByJobId };
    }
    case STARTING('LOAD_JOB_REFRESH_DATES'): {
      const { orgId } = action.payload.args;
      return updateResourceByParam(state, 'jobRefreshDates', 'orgId', orgId, true);
    }
    case SUCCESS('LOAD_JOB_REFRESH_DATES'): {
      const {
        args: { orgId },
        data,
      } = action.payload;
      return updateResourceByParam(state, 'jobRefreshDates', 'orgId', orgId, false, data);
    }
    case FAILURE('LOAD_JOB_REFRESH_DATES'): {
      const { orgId } = action.payload.args;
      return updateResourceByParam(state, 'jobRefreshDates', 'orgId', orgId, false);
    }
    case STARTING('LOAD_ORGANIZATION_DEFAULT_NOTIFICATION_SETTINGS'): {
      const { orgId } = action.payload.args;
      return setStatusByParam(
        state,
        'orgDefaultNotificationSettings',
        'orgId',
        orgId,
        StateStatus.loading,
      );
    }
    case SUCCESS('BULK_UPDATE_ORGANIZATION_DEFAULT_NOTIFICATION_SETTINGS'):
    case SUCCESS('LOAD_ORGANIZATION_DEFAULT_NOTIFICATION_SETTINGS'): {
      const {
        args: { orgId },
        data,
      } = action.payload;
      const newState = setStatusByParam(
        state,
        'orgDefaultNotificationSettings',
        'orgId',
        orgId,
        StateStatus.success,
      );
      return {
        ...newState,
        orgDefaultNotificationSettingsByOrgId: {
          ...newState.orgDefaultNotificationSettingsByOrgId,
          [orgId]: data.settings,
        },
      };
    }
    case FAILURE('LOAD_ORGANIZATION_DEFAULT_NOTIFICATION_SETTINGS'): {
      const { orgId } = action.payload.args;
      return setStatusByParam(
        state,
        'orgDefaultNotificationSettings',
        'orgId',
        orgId,
        StateStatus.error,
      );
    }
    case SUCCESS('UPDATE_ORGANIZATION_DEFAULT_NOTIFICATION_SETTINGS'): {
      const { orgId } = action.payload.args;
      return setStateDirtyByParam(state, 'orgDefaultNotificationSettings', 'orgId', orgId);
    }
    default:
      return state;
  }
};
